import { ethers } from "ethers";
import { DeviceError } from "../errors";
import { splitPath } from "./addresses";
import * as protocol from "../usb/protocol";

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.replace(/^0x/, "");
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * Convert the 65-byte raw signature returned by the Keyfob (v||r||s layout)
 * into an ethers.Signature, normalising high-s and trying both yParity values
 * until the correct sender address is recovered.
 */
export function buildEvmSignature(
  rawBytes: number[] | Uint8Array,
  unsignedTxHash: string,
  senderAddress: string,
): ethers.Signature {
  const b = new Uint8Array(rawBytes);
  const r = ethers.hexlify(b.slice(1, 33));

  const SECP256K1_N = BigInt(
    "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
  );
  let sBigInt = BigInt(ethers.hexlify(b.slice(33, 65)));
  if (sBigInt > SECP256K1_N / BigInt(2)) sBigInt = SECP256K1_N - sBigInt;
  const s = ethers.zeroPadValue(ethers.toBeHex(sBigInt), 32);

  for (const yParity of [0, 1] as (0 | 1)[]) {
    try {
      const sig = ethers.Signature.from({ r, s, yParity });
      const recovered = ethers.recoverAddress(unsignedTxHash, sig);
      if (recovered.toLowerCase() === senderAddress.toLowerCase()) return sig;
    } catch (_) {}
  }

  throw new DeviceError(
    "FAILED_SIGN_TX",
    "Signature recovery failed — device returned an invalid signature.",
  );
}

/**
 * Ask the Keyfob to sign a serialised EIP-1559 transaction.
 * Returns the raw 65-byte signature (v||r||s).
 */
export async function signAndSendEvmTx(
  serializedTx: Uint8Array | Buffer,
  path: string,
  tokenBinary?: string,
): Promise<number[]> {
  const ev = protocol.resetOperation();
  protocol.txBuffer.fill(0);
  protocol.pathBuffer.fill(0);
  protocol.tokenBuffer.fill(0);

  const pathArray = splitPath(path);
  protocol.pathBuffer.set(pathArray);

  const txArray = new Uint8Array(serializedTx);
  protocol.txBuffer.set(txArray);

  const isToken = tokenBinary ? 1 : 0;
  const tokenArray = tokenBinary ? hexToBytes(tokenBinary) : new Uint8Array(0);
  if (tokenBinary) protocol.tokenBuffer.set(tokenArray);

  await protocol.exportWASM?.crypto_guard_if_sign_account_based_tx(
    protocol.ETHEREUM_COIN_TYPE,
    protocol.pathBuffer.byteOffset,
    pathArray.length,
    protocol.txBuffer.byteOffset,
    txArray.length,
    isToken,
    0, // isNewApproche — Solana-only flag; always 0 for EVM
    protocol.tokenBuffer.byteOffset,
    tokenArray.length,
  );
  await new Promise<void>((resolve) => ev.once("ResolveSignTransaction", resolve));

  if (protocol.res.error) throw protocol.res.error;
  return protocol.res.data;
}

/**
 * Re-sign an EVM transaction after a nonce/blockhash expiry.
 * The caller must refresh nonce and gas fields before calling this.
 *
 * `tokenBinary` is re-supplied rather than inherited. resetOperation() does NOT clear
 * protocol.tokenBuffer, so the blob from the interrupted attempt is still sitting there and the
 * retry appeared to work by residual state — which is not something to depend on for what the
 * holder sees while approving a transfer. Re-writing the same buffer is harmless if the firmware
 * ignores it on this opcode, and correct if it does not.
 */
export async function updateEvmTx(
  serializedTx: Uint8Array | Buffer,
  tokenBinary?: string,
): Promise<number[]> {
  const ev = protocol.resetOperation();
  const txArray = new Uint8Array(serializedTx);
  protocol.txBuffer.fill(0);
  protocol.txBuffer.set(txArray);
  protocol.tokenBuffer.fill(0);
  if (tokenBinary) protocol.tokenBuffer.set(hexToBytes(tokenBinary));
  protocol.exportWASM?.crypto_guard_if_update_tx(
    protocol.txBuffer.byteOffset,
    txArray.length,
  );
  await new Promise<void>((resolve) => ev.once("ResolveSignTransaction", resolve));

  if (protocol.res.error) throw protocol.res.error;
  return protocol.res.data;
}
